import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Paper,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import * as mammoth from 'mammoth';

const WordDocumentViewer = ({ 
  open, 
  onClose, 
  documentUrl, 
  documentName, 
  onDownload 
}) => {
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Convert Word document to HTML when URL changes
  useEffect(() => {
    if (open && documentUrl) {
      convertWordToHtml(documentUrl);
    }
  }, [open, documentUrl]);

  const convertWordToHtml = async (url) => {
    setLoading(true);
    setError(null);
    setHtmlContent('');

    try {
      // Fetch the Word document
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.statusText}`);
      }

      // Convert response to array buffer
      const arrayBuffer = await response.arrayBuffer();

      // Use mammoth to convert Word document to HTML
      const result = await mammoth.convertToHtml({ arrayBuffer });
      
      if (result.messages && result.messages.length > 0) {
        console.warn('Mammoth conversion messages:', result.messages);
      }

      setHtmlContent(result.value);
    } catch (err) {
      console.error('Error converting Word document:', err);
      setError(err.message || 'Failed to load Word document');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setHtmlContent('');
    setError(null);
    onClose();
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else if (documentUrl) {
      // Fallback download method
      const link = document.createElement('a');
      link.href = documentUrl;
      link.download = documentName || 'document.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, pr: 2 }}>
            {documentName || 'Word Document'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={handleDownload}
              disabled={!documentUrl}
              title="Download document"
            >
              <DownloadIcon />
            </IconButton>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, height: '100%' }}>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '400px',
              gap: 2,
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="body1">
              Converting Word document...
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              <Typography variant="body2">
                <strong>Unable to display Word document:</strong> {error}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                You can still download the document using the download button.
              </Typography>
            </Alert>
          </Box>
        )}

        {!loading && !error && htmlContent && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              minHeight: '100%',
              '& img': {
                maxWidth: '100%',
                height: 'auto',
              },
              '& table': {
                borderCollapse: 'collapse',
                width: '100%',
                marginBottom: 2,
              },
              '& table, & th, & td': {
                border: '1px solid #ddd',
              },
              '& th, & td': {
                padding: '8px',
                textAlign: 'left',
              },
              '& th': {
                backgroundColor: '#f5f5f5',
              },
              '& p': {
                marginBottom: 1,
                lineHeight: 1.6,
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                marginTop: 2,
                marginBottom: 1,
              },
              '& ul, & ol': {
                paddingLeft: 3,
                marginBottom: 2,
              },
              '& li': {
                marginBottom: 0.5,
              },
            }}
          >
            <div 
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </Paper>
        )}

        {!loading && !error && !htmlContent && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No content to display
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WordDocumentViewer;